import React, { useState } from 'react';
import styled from 'styled-components';
import { FiLock, FiEye, FiEyeOff, FiCheck, FiX } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--background-overlay, rgba(0, 0, 0, 0.5));
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--surface-color, var(--background-primary));
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-lg);
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--control-radius, var(--card-radius));
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  min-width: 44px;
  min-height: 44px;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-primary);
      background: var(--background-secondary);
    }
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--focus-ring);
  }
`;

const Title = styled.h2`
  color: var(--text-primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  text-align: center;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 0.875rem;
  margin: 0 0 2rem 0;
  text-align: center;
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-size: 0.875rem;
  font-weight: 500;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Icon = styled.div`
  position: absolute;
  left: 12px;
  color: var(--text-muted);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  padding: 12px 12px 12px 40px;
  border: 1px solid var(--border-color);
  font-family: 'Inter', sans-serif;
  border-radius: var(--control-radius, var(--card-radius));
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 4px;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--control-radius, var(--card-radius));
  transition: color 0.2s ease;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      color: var(--text-secondary);
    }
  }
`;

const Button = styled.button`
  background: var(--primary-color);
  color: var(--text-inverse);
  border: none;
  border-radius: var(--control-radius, var(--card-radius));
  padding: 12px 24px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 48px;
  width: 100%;

  @media (hover: hover) and (pointer: fine) {
    &:hover:not(:disabled) {
      filter: brightness(0.95);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  &:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }
`;

const ErrorMessage = styled.div`
  background: rgba(220, 53, 69, 0.08);
  color: rgba(220, 53, 69, 0.95);
  padding: 0.75rem;
  border-radius: var(--card-radius);
  font-size: 0.875rem;
  border: 1px solid rgba(220, 53, 69, 0.25);
`;

const SuccessMessage = styled.div`
  background: rgba(56, 161, 105, 0.08);
  color: rgba(56, 161, 105, 0.95);
  padding: 0.75rem;
  border-radius: var(--card-radius);
  font-size: 0.875rem;
  border: 1px solid rgba(56, 161, 105, 0.25);
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const RequirementsList = styled.div`
  background: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--card-radius);
  padding: 0.75rem;
  margin-top: 0.5rem;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: ${props => props.valid ? 'var(--success-color)' : 'var(--text-muted)'};
  margin-bottom: 0.25rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const RequirementIcon = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.valid ? 'var(--success-color)' : 'var(--text-muted)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-inverse);
  font-size: 8px;
  font-weight: bold;
`;

function SimplePasswordChangeModal({ isOpen, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validatePassword = (password) => {
    const requirements = {
      length: password.length >= 10,
      digit: /\d/.test(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)
    };
    return requirements;
  };

  const passwordRequirements = validatePassword(formData.newPassword);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const passwordsMatch = formData.newPassword === formData.confirmPassword && formData.confirmPassword !== '';
  const isDifferentFromCurrent = formData.newPassword !== formData.currentPassword && formData.newPassword !== '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!isPasswordValid) {
      setError('Please ensure your password meets all requirements');
      setLoading(false);
      return;
    }

    if (!passwordsMatch) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (!isDifferentFromCurrent) {
      setError('New password must be different from current password');
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword
      });

      if (response.success) {
        setSuccess('Password changed successfully!');
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setError(response.error || 'Failed to change password');
      }
    } catch (error) {
      logger.error('Password change error', { error });
      setError('Failed to change password. Please try again.');
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            logger.debug('Close button clicked', { onClose: typeof onClose });
            onClose();
          }}
          aria-label="Close modal"
        >
          <FiX size={20} />
        </CloseButton>
        
        <Title>Change Your Password</Title>
        <Subtitle>
          For security reasons, you must change your password before continuing.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Current Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.current ? 'text' : 'password'}
                name="currentPassword"
                placeholder="Enter current password"
                value={formData.currentPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('current')}
                aria-label={showPasswords.current ? 'Hide password' : 'Show password'}
              >
                {showPasswords.current ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
          </FormGroup>

          <FormGroup>
            <Label>New Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.new ? 'text' : 'password'}
                name="newPassword"
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('new')}
                aria-label={showPasswords.new ? 'Hide password' : 'Show password'}
              >
                {showPasswords.new ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
            {formData.newPassword && (
              <RequirementsList>
                <RequirementItem valid={passwordRequirements.length}>
                  <RequirementIcon valid={passwordRequirements.length}>
                    {passwordRequirements.length ? '✓' : '○'}
                  </RequirementIcon>
                  At least 10 characters
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.digit}>
                  <RequirementIcon valid={passwordRequirements.digit}>
                    {passwordRequirements.digit ? '✓' : '○'}
                  </RequirementIcon>
                  At least 1 digit
                </RequirementItem>
                <RequirementItem valid={passwordRequirements.specialChar}>
                  <RequirementIcon valid={passwordRequirements.specialChar}>
                    {passwordRequirements.specialChar ? '✓' : '○'}
                  </RequirementIcon>
                  At least 1 special character
                </RequirementItem>
                <RequirementItem valid={isDifferentFromCurrent}>
                  <RequirementIcon valid={isDifferentFromCurrent}>
                    {isDifferentFromCurrent ? '✓' : '○'}
                  </RequirementIcon>
                  Different from current password
                </RequirementItem>
              </RequirementsList>
            )}
          </FormGroup>

          <FormGroup>
            <Label>Confirm New Password</Label>
            <InputWrapper>
              <Icon>
                <FiLock size={16} />
              </Icon>
              <Input
                type={showPasswords.confirm ? 'text' : 'password'}
                name="confirmPassword"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
              <PasswordToggle
                type="button"
                onClick={() => togglePasswordVisibility('confirm')}
                aria-label={showPasswords.confirm ? 'Hide password' : 'Show password'}
              >
                {showPasswords.confirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </PasswordToggle>
            </InputWrapper>
          </FormGroup>

          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
          {success && <SuccessMessage role="alert">{success}</SuccessMessage>}

          <Button 
            type="submit" 
            disabled={loading || !isPasswordValid || !passwordsMatch || !formData.currentPassword || !isDifferentFromCurrent}
          >
            {loading ? <LoadingSpinner /> : <FiCheck size={16} />}
            {loading ? 'Changing Password...' : 'Change Password'}
          </Button>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
}

export default SimplePasswordChangeModal;
